import { useNavigate } from "@tanstack/react-router";
import { CornerUpLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CachedImage } from "~/components/CachedImage";
import { TEXT } from "~/constants/text";
import { useIsInAppNotificationHidden } from "~/hooks/useIsInAppNotificationHidden";
import { cn } from "~/lib/cn";
import { getAvatarUrl } from "~/lib/imageUrl";
import { HAPTIC_STYLE, native } from "~/lib/nativeBridge";
import { useNotificationStore } from "~/stores/useNotificationStore";
import { isReplyNotification, type Notification } from "~/types/notification";
import { resolveMessagePreview } from "~/utils/resolveMessagePreview";

const DISPLAY_DURATION = 2_000;
const EXIT_DURATION = 300;
const ENTER_DURATION = 300;
const SWIPE_THRESHOLD = 50;

type Phase = "entering" | "visible" | "leaving";
type LeaveDirection = "up" | "right" | "left";
type SwipeAxis = "vertical" | "horizontal" | null;

/**
 * notification.id が変わるたびに ToastContent を再マウントして
 * phase state を常に "entering" から始める
 */
export const InAppNotificationToast = () => {
  const notification = useNotificationStore((s) => s.toastQueue[0]);
  const isHidden = useIsInAppNotificationHidden();
  if (!notification || isHidden) return null;
  return <ToastContent key={notification.id} notification={notification} />;
};

const ToastContent = ({ notification }: { notification: Notification }) => {
  "use no memo";
  const navigate = useNavigate();
  const dequeueToast = useNotificationStore((s) => s.dequeueToast);

  const [phase, setPhase] = useState<Phase>("entering");
  const [leaveDirection, setLeaveDirection] = useState<LeaveDirection>("up");

  const buttonRef = useRef<HTMLButtonElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipeAxisRef = useRef<SwipeAxis>(null);
  // スワイプ中の移動量。re-render を避けるため ref で管理し DOM を直接操作する
  const swipeDeltaRef = useRef({ x: 0, y: 0 });
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = (direction: LeaveDirection) => {
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
    // インラインスタイルをリセットして Tailwind の transition が有効になるようにする
    if (buttonRef.current) {
      buttonRef.current.style.transform = "";
      buttonRef.current.style.transition = "";
    }
    setLeaveDirection(direction);
    setPhase("leaving");
    removeTimerRef.current = setTimeout(() => {
      dequeueToast();
    }, EXIT_DURATION);
  };

  useEffect(() => {
    // entering スタイルをブラウザに確実に描画させてから visible に遷移
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPhase("visible");
      });
    });

    exitTimerRef.current = setTimeout(() => {
      setLeaveDirection("up");
      setPhase("leaving");
    }, ENTER_DURATION + DISPLAY_DURATION);

    removeTimerRef.current = setTimeout(
      () => {
        dequeueToast();
      },
      ENTER_DURATION + DISPLAY_DURATION + EXIT_DURATION,
    );

    return () => {
      cancelAnimationFrame(frameId);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
    };
    // dequeueToast は Zustand アクションで参照安定。ToastContent は notification.id の key で
    // 再マウントされるため、このエフェクトはマウント時の1回のみ実行すればよい
  }, [dequeueToast]);

  const frdId = notification.data?.frd_id ?? "";
  const avatarUrl = getAvatarUrl(notification.data?.ava_id ?? null);
  const isReply = isReplyNotification(notification);

  // ToastContent は notification.id の key で再マウントされるため isReply は変化しない
  useEffect(() => {
    if (isReply) {
      native.triggerHapticFeedback(HAPTIC_STYLE.NOTIFICATION_SUCCESS);
    }
  }, [isReply]);
  const toastMessage = resolveMessagePreview(
    notification.data?.raw_msg ?? notification.message,
    notification.data?.msg_type,
    false,
  );

  const handleClick = () => {
    dequeueToast();
    navigate({
      to: "/messages/$conversationId",
      params: { conversationId: frdId },
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    swipeAxisRef.current = null;
    swipeDeltaRef.current = { x: 0, y: 0 };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current || !buttonRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;

    // 最初に動いた軸を確定して固定する
    if (!swipeAxisRef.current) {
      swipeAxisRef.current =
        Math.abs(dy) >= Math.abs(dx) ? "vertical" : "horizontal";
    }

    let translateX = 0;
    let translateY = 0;

    if (swipeAxisRef.current === "vertical" && dy < 0) {
      translateY = dy;
      swipeDeltaRef.current = { x: 0, y: dy };
    } else if (swipeAxisRef.current === "horizontal") {
      translateX = dx;
      swipeDeltaRef.current = { x: dx, y: 0 };
    }

    if (translateX !== 0 || translateY !== 0) {
      // Tailwind の transition を上書きしてドラッグに即時追従させる
      buttonRef.current.style.transition = "none";
      buttonRef.current.style.transform = `translate(${translateX}px, ${translateY}px)`;
    }
  };

  const handleTouchEnd = () => {
    const { x, y } = swipeDeltaRef.current;
    swipeDeltaRef.current = { x: 0, y: 0 };

    if (y <= -SWIPE_THRESHOLD) {
      dismiss("up");
    } else if (x >= SWIPE_THRESHOLD) {
      dismiss("right");
    } else if (x <= -SWIPE_THRESHOLD) {
      dismiss("left");
    } else if (buttonRef.current) {
      // 閾値未満: インラインスタイルをリセットして元の位置にアニメーションで戻す
      buttonRef.current.style.transition = "";
      buttonRef.current.style.transform = "";
    }

    touchStartRef.current = null;
    swipeAxisRef.current = null;
  };

  return (
    <div className="fixed top-4 left-0 right-0 z-50 flex justify-center pointer-events-none">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={cn(
          "w-[70vw] pointer-events-auto",
          "flex items-center gap-3 px-3 py-3",
          "rounded-2xl shadow-lg text-left",
          // transition は常時適用してフェーズ切り替え時もアニメーションを継続させる
          "transition-[transform,opacity] duration-300",
          // Native の通知バナー風に、BottomNav と同じすりガラス処理で背後を透けさせる
          "backdrop-blur-[22px] backdrop-saturate-[1.8]",
          // 返信の強調背景に -50 を使うと Renka 等のテーマで app.bg と同化するため、
          // 本家 AppToast と同様に ring-2 の縁取り + 一段濃い tint で強調する
          isReply
            ? "bg-cyan-100/[0.8] ring-2 ring-cyan-400"
            : "border-[0.5px] border-cyan-600/10 bg-white/[0.72]",
          phase === "entering" && "-translate-y-full opacity-0 ease-out",
          phase === "visible" &&
            "translate-y-0 translate-x-0 opacity-100 ease-out",
          phase === "leaving" &&
            leaveDirection === "up" &&
            "-translate-y-[150%] opacity-0 ease-in",
          phase === "leaving" &&
            leaveDirection === "right" &&
            "translate-x-[110vw] opacity-0 ease-in",
          phase === "leaving" &&
            leaveDirection === "left" &&
            "-translate-x-[110vw] opacity-0 ease-in",
        )}
      >
        <div className="relative shrink-0">
          <div
            className={cn(
              "size-11 overflow-hidden rounded-full bg-zinc-200",
              isReply && "ring-2 ring-cyan-500",
            )}
          >
            <CachedImage
              src={avatarUrl ?? undefined}
              alt=""
              className="size-full object-cover"
              fallbackClassName="size-full"
            />
          </div>
          {isReply && (
            <div className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-cyan-500">
              <CornerUpLeft className="size-2.5 text-white" strokeWidth={2.5} />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          {isReply && (
            <p className="mb-0.5 line-clamp-2 text-xs font-semibold leading-snug text-cyan-700">
              {TEXT.notification.replyReceived({ name: notification.title })}
            </p>
          )}
          <p
            className={cn(
              "line-clamp-1 break-all text-sm leading-snug",
              isReply ? "text-zinc-600" : "text-zinc-900",
            )}
          >
            {toastMessage}
          </p>
        </div>
      </button>
    </div>
  );
};
